import { useTaxStore, useMonthlyBreakdown } from "../store/useTaxStore";
import { cn } from "@/lib/utils";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function fmt(n: number) {
  return n.toLocaleString("th-TH", { maximumFractionDigits: 0 });
}

export default function RecurringIncomeMonthlySection() {
  const { viewYear } = useTaxStore();
  const breakdown = useMonthlyBreakdown();
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  if (!breakdown) return null;

  const hasAnyIncome = breakdown.months.some((m) => m.total > 0);
  if (!hasAnyIncome) return null;

  const hasFreelance = breakdown.months.some((m) => m.freelance40_8 > 0);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">
          Recurring Income
        </h2>
        <span className="text-xs text-muted-foreground font-sans [font-variant-numeric:tabular-nums]">
          {viewYear}
        </span>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {breakdown.months.map((entry) => {
          const isCurrentMonth =
            viewYear === currentYear && entry.month === currentMonth;
          const isPast =
            viewYear < currentYear ||
            (viewYear === currentYear && entry.month < currentMonth);
          const isEmpty = entry.total === 0;

          return (
            <div
              key={entry.month}
              className={cn(
                "rounded-lg border p-2 space-y-1 transition-colors",
                isCurrentMonth
                  ? "border-primary bg-primary/5"
                  : "border-border bg-background",
                isPast && isEmpty && "opacity-40",
              )}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="text-[11px] font-medium text-muted-foreground">
                  {MONTH_NAMES[entry.month - 1]}
                </span>
                {entry.isProjected && entry.total > 0 && (
                  <span className="text-[9px] text-muted-foreground bg-muted rounded px-1">
                    est
                  </span>
                )}
              </div>

              <div className="font-sans [font-variant-numeric:tabular-nums]">
                <p className="text-[12px] font-semibold text-foreground leading-tight">
                  {isEmpty ? "—" : `฿${fmt(entry.total)}`}
                </p>
                {!isEmpty && (
                  <div className="space-y-0.5 mt-1">
                    {entry.employment40_1 > 0 && (
                      <p className="text-[10px] text-muted-foreground">
                        40(1) ฿{fmt(entry.employment40_1)}
                      </p>
                    )}
                    {hasFreelance && entry.freelance40_8 > 0 && (
                      <p className="text-[10px] text-muted-foreground">
                        40(8) ฿{fmt(entry.freelance40_8)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-border">
        <span className="text-xs text-muted-foreground">Annual total</span>
        <span className="text-sm font-semibold font-sans [font-variant-numeric:tabular-nums] text-foreground">
          ฿{fmt(breakdown.months.reduce((sum, m) => sum + m.total, 0))}
        </span>
      </div>
    </div>
  );
}
